import PropTypes from 'prop-types'
import {Box, Text, useBreakpointValue} from '@chakra-ui/react'
import {get, snakeCase} from 'lodash-es'
import React from 'react'
import i18next from 'i18next'
import useSWR from 'swr'
import {useTranslation} from 'react-i18next'

import UpdatePayeeModal from '@pointsOnPayments/components/UpdatePayeeModal'
import useFeatureFlags from '../../hooks/useFeatureFlags'
import {camelizeResult, dineroMoneyObjects} from '../../../api/serializer'
import {fetcher} from '../../../api'
import {EmptyList, Pagination} from '..'
import Large from './Large'
import Medium from './Medium'

const PER_PAGE = 25

export const BillsTable = ({supplierId}) => {
  i18next.loadNamespaces('pointsOnPayments')
  const {t} = useTranslation()

  const [page, setPage] = React.useState(1)
  React.useEffect(() => setPage(1), [])

  const [sortBy, setSortBy] = React.useState(null)
  const [orderBy, setOrderBy] = React.useState(null)
  const handleOnSortTable = ([sort]) => {
    if (!sort) {
      setSortBy(null)
      setOrderBy(null)
    } else {
      if (sort.id === 'canAddOverdueInfo') {
        return
      }

      setSortBy(snakeCase(sort.id))
      setOrderBy(sort.desc ? 'desc' : 'asc')
    }
  }

  const constructUrl = React.useMemo(() => {
    let url = `/points_on_payments/api/suppliers/${supplierId}/bills?page=${page}&per_page=${PER_PAGE}`

    if (sortBy) url = `${url}&sort_by=${sortBy}`
    if (orderBy) url = `${url}&order_by=${orderBy}`
    return url
  }, [page, sortBy, orderBy, supplierId])

  const {data, isLoading, error, mutate} = useSWR(constructUrl, fetcher, {
    use: [camelizeResult, dineroMoneyObjects],
    keepPreviousData: true,
  })

  if (error && error.message === 'Unauthorized') {
    window.location.href = '/sign_in'
  }

  const totalCount = get(data, 'metadata.totalCount', 0)
  const totalPages = get(data, 'metadata.totalPages', 0)
  const bills = data?.payables || []

  const {loading: loadingFeatures} = useFeatureFlags()

  const loading = isLoading || loadingFeatures
  const emptyList = !bills.length && !loading

  const [selectedPayee, setSelectedPayee] = React.useState(null)

  const handleClosePayeeModal = React.useCallback(
    () => setSelectedPayee(null),
    [],
  )
  const handlePayeeUpdated = React.useCallback(() => {
    setSelectedPayee(null)
    mutate()
  }, [mutate])

  const isLarge = useBreakpointValue({
    base: false,
    md: true,
  })

  return (
    <Box id="bills-table" data-testid="bills-table">
      {selectedPayee && (
        <UpdatePayeeModal
          isOpen
          onClose={handleClosePayeeModal}
          payee={selectedPayee}
          onPayeeUpdated={handlePayeeUpdated}
        />
      )}

      {error && (
        <Text ml="md">
          {t('pointsOnPayments:bill.table.failedFetchingBills')}
        </Text>
      )}

      {isLarge ? (
        <Large
          bills={bills}
          isLoading={loading}
          onSelectPayee={setSelectedPayee}
          onSort={handleOnSortTable}
        />
      ) : (
        <Medium
          bills={bills}
          isLoading={loading}
          onSelectPayee={setSelectedPayee}
        />
      )}

      {emptyList && <EmptyList resourceName="bills" />}

      {!emptyList && (
        <Box px="md" mb="md" position="sticky" left="0">
          <Pagination
            totalPages={totalPages}
            currentPage={page}
            totalCount={totalCount}
            perPage={PER_PAGE}
            onPageChange={setPage}
          />
        </Box>
      )}
    </Box>
  )
}

BillsTable.propTypes = {
  supplierId: PropTypes.string.isRequired,
}

export default BillsTable
